import dayjs from "dayjs";
import moment from "moment";

function getCurrentMonth(calendarEndDate) {
  return moment(calendarEndDate).format("MMM, YYYY");
}
function getAllTime(firstDataDate, calendarEndDate, hideMonths = false) {
  let range = "All Time";

  if (hideMonths) {
    return range;
  }

  const beginning = moment(firstDataDate).format("MMM, YYYY");
  const end = getCurrentMonth(calendarEndDate);

  return `${range} | ${beginning} - ${end}`;
}
function getLastYear(calendarEndDate) {
  const startOfLastYear = moment(calendarEndDate)
    .startOf("year")
    .subtract(1, "year")
    .format("MMM, YYYY");
  const endOfLastYear = moment(calendarEndDate)
    .endOf("year")
    .subtract(1, "year")
    .format("MMM, YYYY");

  return `Last Year | ${startOfLastYear} - ${endOfLastYear}`;
}
function getLastQuarter(calendarEndDate, hideMonths) {
  let range = "Last Quarter";

  if (hideMonths) {
    return range;
  }

  const startOfQuarter = moment(calendarEndDate)
    .startOf("quarter")
    .subtract(1, "quarter")
    .format("MMM, YYYY");
  const endOfQuarter = moment(calendarEndDate)
    .endOf("quarter")
    .subtract(1, "quarter")
    .format("MMM, YYYY");

  return `${range} | ${startOfQuarter} - ${endOfQuarter}`;
}
export function getThisYear(calendarEndDate, hideMonths) {
  let range = "This Year";

  if (hideMonths) {
    return range;
  }

  const startOfYear = moment(calendarEndDate).startOf("year").format("MMM, YYYY");

  return `${range} | ${startOfYear} - ${getCurrentMonth(calendarEndDate)}`;
}
function getLastSixMonths(calendarEndDate, hideMonths) {
  let range = "Last 6 Months";

  if (hideMonths) {
    return range;
  }

  const sixMonthsAgo = moment(calendarEndDate).subtract(6, "months").format("MMM, YYYY");
  const endOfLastMonth = moment(calendarEndDate)
    .endOf("month")
    .subtract(1, "month")
    .format("MMM, YYYY");

  return `${range} | ${sixMonthsAgo} - ${endOfLastMonth}`;
}
function getLastThreeMonths(calendarEndDate, hideMonths) {
  let range = "Last 3 Months";

  if (hideMonths) {
    return range;
  }

  const threeMonthsAgo = moment(calendarEndDate).subtract(3, "months").format("MMM, YYYY");
  const endOfLastMonth = moment(calendarEndDate)
    .endOf("month")
    .subtract(1, "month")
    .format("MMM, YYYY");

  return `${range} | ${threeMonthsAgo} - ${endOfLastMonth}`;
}
export function getMonthToDate(calendarEndDate, hideMonths) {
  let range = "Month To Date";

  if (hideMonths) {
    return range;
  }

  return `${range} | ${getCurrentMonth(calendarEndDate)}`;
}
function getLastMonth(calendarEndDate) {
  const startOfLastMonth = moment(calendarEndDate)
    .startOf("month")
    .subtract(1, "month")
    .format("MMM, YYYY");
  const endOfLastMonth = moment(calendarEndDate)
    .endOf("month")
    .subtract(1, "month")
    .format("MMM, YYYY");

  return `Last Month | ${startOfLastMonth} - ${endOfLastMonth}`;
}
function getLastThirtyDays(calendarEndDate, hideMonths) {
  let range = "Last 30 Days";

  if (hideMonths) {
    return range;
  }

  const last30Days = moment(calendarEndDate).subtract(30, "days").format("MMM, YYYY");

  return `${range} | ${last30Days} - ${getCurrentMonth(calendarEndDate)}`;
}
export function getCustomRange(hideMonths, startDate, endDate) {
  let range = "Custom Range";

  if (hideMonths) {
    return range;
  }

  return `Custom Range | ${moment(startDate).format("MMM DD, YYYY")} - ${moment(endDate).format(
    "MMM DD, YYYY"
  )}`;
}
export function getLastSixtyDays() {
  return `Last 60 Days`;
}

export const getSelectedRangeOption = (calendarEndDate) => ({
  id: 8,
  name: getMonthToDate(calendarEndDate)
});

/**
 *
 * @param {string} calendarEndDate The minimum report date in the db
 * @param {boolean} hideMonths Hide the month in the label
 * @param {string} firstDataDate Date used to calculate all time
 * @returns {array} [{ id, name }]
 * if hideMonths is true then: [{ id: 1, name: 'This Year'}]
 * if hideMonths is false then: [{ id: 1, name: 'This Year | Jan 2020 - Sep 2020'}]
 */
export const getRangeOptions = (calendarEndDate, firstDataDate, hideMonths) => {
  const initData = [];

  if (firstDataDate) {
    initData.push(getAllTime(firstDataDate, calendarEndDate, hideMonths));
  }

  if (hideMonths) {
    // 1 >= i <= 7
    return [
      ...initData,
      getThisYear(calendarEndDate, true),
      getLastQuarter(calendarEndDate, true),
      getLastSixMonths(calendarEndDate, true),
      getLastThreeMonths(calendarEndDate, true),
      getLastSixtyDays(calendarEndDate, true),
      getLastThirtyDays(calendarEndDate, true),
      getMonthToDate(calendarEndDate, true)
    ].map((name, i) => ({
      id: firstDataDate ? i : i + 1,
      name
    }));
  }

  // 1 >= i <= 8
  return [
    ...initData,
    getLastYear(calendarEndDate),
    getThisYear(calendarEndDate),
    getLastQuarter(calendarEndDate),
    getLastSixMonths(calendarEndDate),
    getLastThreeMonths(calendarEndDate),
    getLastThirtyDays(calendarEndDate),
    getLastMonth(calendarEndDate),
    getMonthToDate(calendarEndDate)
  ].map((name, i) => ({
    id: firstDataDate ? i : i + 1,
    name
  }));
};

export const getRangeOptionsWithCustom = (dateRangeOptions, action, hideMonths) => {
  const rangeOptions = Array.isArray(dateRangeOptions) ? dateRangeOptions : [];
  return [
    ...rangeOptions.filter((option) => !option.name.includes("Custom Range")),
    getSelectedRangeOptionWithCustom(action, hideMonths)
  ];
};

export const getSelectedRangeOptionWithCustom = (action, hideMonths) => {
  const { startDate, endDate } = action.payload;
  return {
    id: 999,
    name: getCustomRange(hideMonths, startDate, endDate),
    startDate,
    endDate
  };
};

export const addAllTime = (state, action, hideMonths) => {
  const ranges = [
    ...(state.rangeOptions || getRangeOptions(state.currentUser.calendarEndDate, hideMonths))
  ];
  const hasAllTime = ranges.some((range) => range.id === 0);

  if (!hasAllTime) {
    ranges.unshift({
      id: 0,
      name: getAllTime(action.payload, state.currentUser.calendarEndDate, hideMonths)
    });
  }
  return ranges;
};
export const getDateRanges = (
  calendarEndDate,
  data,
  firstDataDate,
  hideMonths,
  subtractMonthAmount = 1
) => {
  let start;
  let end;

  if (data.name === getThisYear(calendarEndDate, hideMonths)) {
    start = moment(calendarEndDate).startOf("year").format("YYYY-MM-DD");
    end = moment(calendarEndDate).format("YYYY-MM-DD");
  }

  if (firstDataDate && data.name === getAllTime(firstDataDate, calendarEndDate, hideMonths)) {
    start = firstDataDate;
    end = moment(calendarEndDate).format("YYYY-MM-DD");
  }

  if (data.name === getLastYear(calendarEndDate)) {
    start = moment(calendarEndDate).startOf("year").subtract(1, "year").format("YYYY-MM-DD");
    end = moment(calendarEndDate).subtract(1, "year").endOf("year").format("YYYY-MM-DD");
  }

  if (data.name === getLastMonth(calendarEndDate)) {
    start = moment(calendarEndDate).startOf("month").subtract(1, "month").format("YYYY-MM-DD");
    end = moment(calendarEndDate).subtract(1, "month").endOf("month").format("YYYY-MM-DD");
  }

  if (data.name === getLastQuarter(calendarEndDate, hideMonths)) {
    start = moment(calendarEndDate).startOf("quarter").subtract(1, "quarter").format("YYYY-MM-DD");
    end = moment(calendarEndDate).subtract(1, "quarter").endOf("quarter").format("YYYY-MM-DD");
  }

  if (data.name === getLastSixMonths(calendarEndDate, hideMonths, subtractMonthAmount)) {
    start = moment(calendarEndDate)
      .subtract(subtractMonthAmount === 0 ? 5 : 6, "months")
      .startOf("month")
      .format("YYYY-MM-DD");
    end = moment(calendarEndDate)
      .subtract(subtractMonthAmount, "months")
      .endOf("month")
      .format("YYYY-MM-DD");
  }

  if (data.name === getLastThreeMonths(calendarEndDate, hideMonths, subtractMonthAmount)) {
    start = moment(calendarEndDate)
      .subtract(subtractMonthAmount === 0 ? 2 : 3, "months")
      .startOf("month")
      .format("YYYY-MM-DD");
    end = moment(calendarEndDate)
      .subtract(subtractMonthAmount, "months")
      .endOf("month")
      .format("YYYY-MM-DD");
  }

  if (data.name === getLastThirtyDays(calendarEndDate, hideMonths)) {
    start = moment(calendarEndDate).subtract(29, "days").format("YYYY-MM-DD");
    end = calendarEndDate;
  }

  if (data.name === getLastSixtyDays()) {
    start = moment(calendarEndDate).subtract(60, "days").format("YYYY-MM-DD");
    end = calendarEndDate;
  }

  if (data.name === getMonthToDate(calendarEndDate, hideMonths)) {
    start = moment(calendarEndDate).startOf("month").format("YYYY-MM-DD");
    end = calendarEndDate;
  }

  if (data.name.includes("Custom Range")) {
    start = data.startDate;
    end = data.endDate;
  }

  if (dayjs(start).isBefore(dayjs(firstDataDate))) {
    start = firstDataDate;
  }

  return {
    startDate: start,
    endDate: end
  };
};

const getDiffDays = (currentStartDate, currentEndDate) => {
  const startDate = dayjs(currentStartDate);
  const endDate = dayjs(currentEndDate);

  return endDate.diff(startDate, "day");
};

export const getPreviousFilterDates = (
  currentStartDate,
  currentEndDate,
  calendarStartDate,
  dateRange = ""
) => {
  if (dateRange.includes("All Time") || currentStartDate === calendarStartDate) return {};

  const diff = getDiffDays(currentStartDate, currentEndDate);
  const prevStartDate = dayjs(currentStartDate)
    .subtract(diff + 1, "day")
    .format("YYYY-MM-DD");

  return {
    prevStartDate: dayjs(prevStartDate).isBefore(calendarStartDate)
      ? calendarStartDate
      : prevStartDate,
    prevEndDate: dayjs(currentStartDate).subtract(1, "day").format("YYYY-MM-DD"),
    prevCalendarEndDate: dayjs(currentStartDate).subtract(1, "day").format("YYYY-MM-DD")
  };
};
